/** @jsxImportSource react */
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import type { Notification } from '@/contexts/NotificationContext';
import { useThemeLanguage } from '@/contexts/ThemeLanguageContext';
import {
  formatNotificationDateTime,
  formatNotificationTime,
} from '@/utils/formatNotificationTime';
import { AdminPagination } from '@/views-react/shared/admin/AdminPagination';
import '@/views-react/shared/admin/AdminPagination.css';
import { getNotificationIcon } from './NotificationIcons';
import './NotificationListPanel.css';

export type NotificationListVariant = 'dropdown' | 'full';

const NOTIFICATION_PAGE_SIZE_OPTIONS = [10, 25, 50];

export interface NotificationListPanelProps {
  notifications: Notification[];
  variant?: NotificationListVariant;
  emptyLabelKey?: string;
  onMarkRead?: (id: string) => void;
  onMarkAllRead?: () => void;
  onClearAll?: () => void;
  onItemClick?: (notification: Notification) => void;
  showActions?: boolean;
  clearConfirmKey?: string;
  paginationSummaryKey?: string;
}

export function NotificationListPanel({
  notifications,
  variant = 'dropdown',
  emptyLabelKey = 'admin.noNotifications',
  onMarkRead,
  onMarkAllRead,
  onClearAll,
  onItemClick,
  showActions = false,
  clearConfirmKey = 'admin.config.deleteConfirm',
  paginationSummaryKey = 'settings.notifications.paginationShowing',
}: NotificationListPanelProps) {
  const { t } = useThemeLanguage();
  const [pageOffset, setPageOffset] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    setPageOffset(0);
  }, [notifications.length, pageSize]);

  useEffect(() => {
    if (pageOffset >= notifications.length && pageOffset > 0) {
      setPageOffset(Math.max(0, notifications.length - pageSize));
    }
  }, [notifications.length, pageOffset, pageSize]);

  const paginatedNotifications = useMemo(() => {
    if (variant !== 'full') return notifications;
    return notifications.slice(pageOffset, pageOffset + pageSize);
  }, [notifications, variant, pageOffset, pageSize]);

  if (notifications.length === 0) {
    return <p className={variant === 'full' ? 'admin-muted' : 'notification-empty'}>{t(emptyLabelKey)}</p>;
  }

  const listClass = variant === 'full' ? 'admin-config-notifications-list' : 'notification-list';

  const actionsBlock =
    showActions && (onMarkAllRead || onClearAll) ? (
      <div className={variant === 'full' ? 'admin-config-notifications-actions' : 'notification-dropdown-header'}>
        {variant === 'dropdown' && <h3>{t('layout.notificationsHeader')}</h3>}
        <div className="notification-list-actions">
          {onMarkAllRead && (
            <button type="button" className="admin-btn" onClick={onMarkAllRead}>
              {t(variant === 'full' ? 'admin.markAllRead' : 'layout.markAllRead')}
            </button>
          )}
          {onClearAll && (
            <button
              type="button"
              className="admin-btn"
              onClick={() => {
                if (window.confirm(t(clearConfirmKey))) onClearAll();
              }}
            >
              {t('admin.config.deleteAll')}
            </button>
          )}
        </div>
      </div>
    ) : null;

  const listBlock = (
    <div className={listClass} data-testid={variant === 'full' ? 'notification-list-panel-scroll' : undefined}>
      {paginatedNotifications.map((n) => (
        <NotificationListItem
          key={n.id}
          notification={n}
          variant={variant}
          onMarkRead={onMarkRead}
          onClick={onItemClick ? () => onItemClick(n) : undefined}
        />
      ))}
    </div>
  );

  if (variant !== 'full') {
    return (
      <>
        {actionsBlock}
        {listBlock}
      </>
    );
  }

  return (
    <div className="notification-list-panel-full" data-testid="notification-list-panel-full">
      {actionsBlock}
      <div className="notification-list-panel-full-scroll">{listBlock}</div>
      <div className="notification-list-panel-full-pagination">
        <AdminPagination
            offset={pageOffset}
            limit={pageSize}
            total={notifications.length}
            itemsLength={paginatedNotifications.length}
            onPrev={() => setPageOffset((o) => Math.max(0, o - pageSize))}
            onNext={() => setPageOffset((o) => o + pageSize)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageOffset(0);
            }}
            pageSizeOptions={NOTIFICATION_PAGE_SIZE_OPTIONS}
            paginationSummaryKey={paginationSummaryKey}
          />
      </div>
    </div>
  );
}

function NotificationListItem({
  notification: n,
  variant,
  onMarkRead,
  onClick,
}: {
  notification: Notification;
  variant: NotificationListVariant;
  onMarkRead?: (id: string) => void;
  onClick?: () => void;
}) {
  const { t, lang } = useThemeLanguage();
  const isFull = variant === 'full';
  const itemClass = isFull
    ? `admin-config-notification-card ${!n.read ? 'unread' : ''}`
    : `notification-item ${!n.read ? 'unread' : ''}`;

  const handleClick = () => {
    if (!n.read && onMarkRead) onMarkRead(n.id);
    onClick?.();
  };

  const cardInteractiveProps = onClick
    ? {
        onClick: handleClick,
        role: 'button' as const,
        tabIndex: 0,
        onKeyDown: (e: KeyboardEvent) => {
          if (e.key === 'Enter') handleClick();
        },
      }
    : {};

  if (isFull) {
    const isApp = n.type === 'application';
    return (
      <div className={itemClass} {...cardInteractiveProps}>
        <div className="admin-config-notification-header">
          <span className="admin-config-notification-icon">{getNotificationIcon(n.type)}</span>
          <span className="admin-config-notification-title">{n.titleKey ? t(n.titleKey) : n.title}</span>
          <span className="admin-config-notification-time">
            {formatNotificationDateTime(new Date(n.timestamp), lang)}
          </span>
          {!n.read && onMarkRead && (
            <button
              type="button"
              className="admin-btn admin-btn-small"
              onClick={(e) => {
                e.stopPropagation();
                onMarkRead(n.id);
              }}
            >
              {t('admin.config.markRead')}
            </button>
          )}
        </div>
        <p className="admin-config-notification-message">{n.messageKey ? t(n.messageKey) : n.message}</p>
        {isApp && (n.vacanteTitle || n.vacanteId || n.candidateName || n.candidateEmail) && (
          <div className="admin-config-notification-detail">
            {n.vacanteTitle && (
              <div>
                <strong>{t('admin.config.labelVacancy')}:</strong> {n.vacanteTitle}
              </div>
            )}
            {n.vacanteId && (
              <div>
                <strong>{t('admin.config.labelIdVacancy')}:</strong> <code>{n.vacanteId}</code>
              </div>
            )}
            {n.candidateName && (
              <div>
                <strong>{t('admin.config.labelCandidate')}:</strong> {n.candidateName}
              </div>
            )}
            {n.candidateEmail && (
              <div>
                <strong>{t('admin.config.labelEmail')}:</strong> {n.candidateEmail}
              </div>
            )}
            {n.applicationId && (
              <div>
                <strong>{t('admin.config.labelIdApp')}:</strong> <code>{n.applicationId}</code>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={itemClass}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
    >
      <div className="notification-item-icon">{getNotificationIcon(n.type)}</div>
      <div className="notification-item-content">
        <p className="notification-title">{n.titleKey ? t(n.titleKey) : n.title}</p>
        <p className="notification-description">{n.messageKey ? t(n.messageKey) : n.message}</p>
        <span className="notification-time">
          {formatNotificationTime(new Date(n.timestamp), lang)}
        </span>
      </div>
    </div>
  );
}
